'use client';

import { c as _c } from "react/compiler-runtime";
import { TOTAL_AI_SPEND_CARD_TOOLTIP } from '@/domains/pages/ai-spend/constants';
import type { AiSpendOrgSnapshot } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { NebulyPageCard } from '@/domains/pages/shared/nebuly-page-card';
import { getPageTimeRangePhrase } from '@/domains/pages/shared/page-time-range';
import { PAGE_DATA_NUMBER_CLASS, PAGE_KPI_NUMBER_CLASS } from '@/domains/pages/shared/typography';
import type { TimeRangeConfig } from '@/api/generated/model';
import { formatSpend } from '@/utils/format.util';
import { Button } from '@/comps/primitives/button';
import { cn } from '@/utils/shad';
import { ArrowRight } from 'lucide-react';
type TotalAiSpendCardProps = {
  org: AiSpendOrgSnapshot;
  timeRange: TimeRangeConfig | undefined;
  onOpenBreakdown: () => void;
  className?: string;
};
export function TotalAiSpendCard(t0) {
  const $ = _c(25);
  const {
    org,
    timeRange,
    onOpenBreakdown,
    className
  } = t0;
  let t1;
  if ($[0] !== timeRange) {
    t1 = getPageTimeRangePhrase(timeRange);
    $[0] = timeRange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const periodLabel = t1;
  const t2 = org.totalCost == null;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArrowRight className="size-3" strokeWidth={2} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onOpenBreakdown || $[4] !== t2) {
    t4 = <Button variant="outline" size="sm" className="h-7 gap-1.5 rounded-full px-3 text-xs text-muted-foreground hover:text-foreground" onClick={onOpenBreakdown} disabled={t2}>Breakdown{t3}</Button>;
    $[3] = onOpenBreakdown;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== className) {
    t5 = cn("min-h-0", className);
    $[6] = className;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== org.totalCost) {
    t6 = formatSpend(org.totalCost, {
      precision: "exact"
    });
    $[8] = org.totalCost;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== org.totalCost) {
    t7 = formatSpend(org.totalCost);
    $[10] = org.totalCost;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <span className={PAGE_KPI_NUMBER_CLASS} title={t6}>{t7}</span>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("pb-1 text-[14px] leading-none text-muted-foreground", PAGE_DATA_NUMBER_CLASS);
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== periodLabel) {
    t10 = <span className={t9}>{periodLabel}</span>;
    $[16] = periodLabel;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t8) {
    t11 = <div className="flex flex-wrap items-end gap-3">{t8}{t10}</div>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t4 || $[23] !== t5) {
    t12 = <NebulyPageCard title="Total AI spend" description={TOTAL_AI_SPEND_CARD_TOOLTIP} headerTrailing={t4} className={t5} contentClassName="flex flex-col gap-3 !py-3" testId="total-ai-spend-card">{t11}</NebulyPageCard>;
    $[21] = t11;
    $[22] = t4;
    $[23] = t5;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}